import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { API } from '../config/api';
import { safeJson } from '../utils/safeJson';

import { 
  Users, Plus, Trash2, Save, ArrowLeft, Loader2, 
  Shield, Star, Swords, MessageSquare, Calendar, Trophy, 
  FileText, Search, X, Check
} from 'lucide-react';


// Permission definitions
const PERMISSIONS = [
  { 
    key: 'manage_featured', 
    label: 'Feature Content Updates', 
    description: 'Update homepage featured sections',
    icon: Star,
    color: 'text-yellow-500'
  },
  { 
    key: 'initiate_battles', 
    label: 'Initiate Cypher Battles', 
    description: 'Create and launch new battles',
    icon: Swords,
    color: 'text-orange-500'
  },
  { 
    key: 'moderate_battles', 
    label: 'Moderate Battles', 
    description: 'Moderate battle entries and votes',
    icon: Shield,
    color: 'text-red-500'
  },
  { 
    key: 'manage_spotlight', 
    label: 'Manage Community Spotlight', 
    description: 'Add and edit community spotlights',
    icon: Star,
    color: 'text-purple-500'
  },
  { 
    key: 'manage_creatives', 
    label: 'Manage Creatives Listings', 
    description: 'Manage creative directory listings',
    icon: Users,
    color: 'text-blue-500'
  },
  { 
    key: 'manage_events', 
    label: 'Manage Events', 
    description: 'Create and edit platform events',
    icon: Calendar,
    color: 'text-green-500'
  },
  { 
    key: 'manage_charts', 
    label: 'Manage Charts', 
    description: 'Manage music and video charts',
    icon: Trophy,
    color: 'text-cyan-500'
  }
];

export default function AdminRolesPage() {
  const navigate = useNavigate();
  const { user, getAuthHeaders, isAuthenticated } = useAuth();
  const [adminRoles, setAdminRoles] = useState([]);
  const [auditLog, setAuditLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [searchEmail, setSearchEmail] = useState('');
  
  const [formData, setFormData] = useState({
    email: '',
    permissions: {}
  });

  // Check owner access only
  useEffect(() => {
    if (!isAuthenticated || user?.role !== 'owner') {
      toast.error('Owner access required');
      navigate('/');
    }
  }, [isAuthenticated, user, navigate]);

  useEffect(() => {
    fetchAdminRoles();
    fetchAuditLog();
  }, []);

  const fetchAdminRoles = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/collateral-roles`, {
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setAdminRoles(data.roles || []);
      }
    } catch (err) {
      console.error('Failed to fetch admin roles:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAuditLog = async () => {
    try {
      const res = await fetch(`${API}/admin/collateral-roles/audit`, {
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setAuditLog(data.logs || []);
      }
    } catch (err) {
      console.error('Failed to fetch audit log:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.email.trim() || !formData.email.includes('@')) {
      toast.error('Valid email is required');
      return;
    }

    // Check at least one permission is selected
    const hasPermission = Object.values(formData.permissions).some(v => v);
    if (!hasPermission) {
      toast.error('Select at least one permission');
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API}/admin/collateral-roles`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: formData.email.toLowerCase().trim(),
          permissions: formData.permissions
        })
      });

      if (res.ok) {
        toast.success('Admin role assigned!');
        resetForm();
        fetchAdminRoles();
        fetchAuditLog();
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Failed to assign role');
      }
    } catch (err) {
      toast.error('Failed to assign role');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRevoke = async (email) => {
    if (!confirm(`Revoke all admin permissions for ${email}?`)) return;

    try {
      const res = await fetch(`${API}/admin/collateral-roles/${encodeURIComponent(email)}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (res.ok) {
        toast.success('Admin role revoked');
        fetchAdminRoles();
        fetchAuditLog();
      } else {
        toast.error('Failed to revoke role');
      }
    } catch (err) {
      toast.error('Failed to revoke role');
    }
  };

  const handleUpdatePermission = async (email, permKey, enabled) => {
    try {
      const res = await fetch(`${API}/admin/collateral-roles/permission`, {
        method: 'PATCH',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, permission: permKey, enabled })
      });

      if (res.ok) {
        toast.success('Permission updated');
        fetchAdminRoles();
        fetchAuditLog();
      } else {
        toast.error('Failed to update permission');
      }
    } catch (err) {
      toast.error('Failed to update permission');
    }
  };

  const resetForm = () => {
    setFormData({ email: '', permissions: {} });
    setShowForm(false);
  };

  const togglePermission = (key) => {
    setFormData(prev => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [key]: !prev.permissions[key]
      }
    }));
  };

  const filteredRoles = searchEmail 
    ? adminRoles.filter(r => r.email.toLowerCase().includes(searchEmail.toLowerCase()))
    : adminRoles;

  return (
    <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-8" data-testid="admin-roles-page">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="font-heading text-3xl font-bold">Collateral Duty Roles</h1>
            <p className="text-muted-foreground">Assign permission-based admin access by email</p>
          </div>
          <Button 
            onClick={() => setShowForm(true)}
            className="ml-auto"
            data-testid="add-role-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Assign Role
          </Button>
        </div>

        {/* Add Role Form */}
        {showForm && (
          <Card className="glass-card border-border p-6 mb-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-xl font-bold">Assign Admin Role</h2>
              <Button variant="ghost" size="icon" onClick={resetForm}>
                <X className="w-5 h-5" />
              </Button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label>Email Address *</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="user@example.com"
                  className="bg-muted/30 border-border"
                  data-testid="role-email-input"
                />
              </div>

              <div>
                <Label className="mb-4 block">Permissions</Label>
                <div className="grid md:grid-cols-2 gap-3">
                  {PERMISSIONS.map(perm => {
                    const IconComponent = perm.icon;
                    return (
                      <div 
                        key={perm.key}
                        onClick={() => togglePermission(perm.key)}
                        className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                          formData.permissions[perm.key] 
                            ? 'border-primary bg-primary/10' 
                            : 'border-border hover:border-border'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full bg-muted/30 flex items-center justify-center ${perm.color}`}>
                            <IconComponent className="w-5 h-5" />
                          </div>
                          <div className="flex-1">
                            <div className="font-medium">{perm.label}</div>
                            <div className="text-xs text-muted-foreground">{perm.description}</div>
                          </div>
                          {formData.permissions[perm.key] && (
                            <Check className="w-5 h-5 text-primary" />
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="flex gap-3">
                <Button type="submit" disabled={submitting}>
                  {submitting ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Assign Role
                </Button>
                <Button type="button" variant="outline" onClick={resetForm}>
                  Cancel
                </Button>
              </div>
            </form>
          </Card>
        )}

        {/* Search */}
        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search by email..."
            value={searchEmail}
            onChange={(e) => setSearchEmail(e.target.value)}
            className="pl-10 bg-muted/30 border-border"
          />
        </div>

        {/* Roles List */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin" />
          </div>
        ) : filteredRoles.length > 0 ? (
          <div className="space-y-4">
            {filteredRoles.map(role => (
              <Card key={role.email} className="glass-card border-border p-4">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <div className="flex items-center gap-2">
                      <Shield className="w-5 h-5 text-primary" />
                      <span className="font-medium">{role.email}</span>
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      Added {new Date(role.created_at).toLocaleDateString()}
                    </div>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    className="text-destructive"
                    onClick={() => handleRevoke(role.email)}
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Revoke All
                  </Button>
                </div>

                <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-3">
                  {PERMISSIONS.map(perm => {
                    const IconComponent = perm.icon;
                    const isEnabled = role.permissions?.[perm.key];
                    return (
                      <div 
                        key={perm.key}
                        className="flex items-center justify-between p-2 rounded-lg bg-muted/30"
                      >
                        <div className="flex items-center gap-2">
                          <IconComponent className={`w-4 h-4 ${perm.color}`} />
                          <span className="text-sm">{perm.label}</span>
                        </div>
                        <Switch
                          checked={isEnabled}
                          onCheckedChange={(checked) => handleUpdatePermission(role.email, perm.key, checked)}
                        />
                      </div>
                    );
                  })}
                </div>
              </Card>
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <Shield className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <h2 className="font-heading text-xl font-bold mb-2">No Admin Roles Assigned</h2>
            <p className="text-muted-foreground mb-4">
              Assign collateral duty roles to trusted users.
            </p>
            <Button onClick={() => setShowForm(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Assign First Role
            </Button>
          </div>
        )}

        {/* Audit Log */}
        {auditLog.length > 0 && (
          <div className="mt-12">
            <h2 className="font-heading text-xl font-bold mb-4">Recent Activity</h2>
            <Card className="glass-card border-border p-4">
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {auditLog.slice(0, 20).map((log, idx) => (
                  <div key={idx} className="flex items-center gap-3 text-sm py-2 border-b border-border last:border-0">
                    <Badge variant="outline" className="text-xs">
                      {log.action}
                    </Badge>
                    <span className="text-muted-foreground">{log.target_email}</span>
                    <span className="text-xs text-muted-foreground ml-auto">
                      {new Date(log.timestamp).toLocaleString()}
                    </span>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
